import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Créer une instance axios avec configuration par défaut
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token à chaque requête
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getLoginUrl: () => api.get('/auth/login-url'),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  localLogin: (username, password) => api.post('/auth/local-login', { username, password })
};

// Franchises API
export const franchisesAPI = {
  getAll: () => api.get('/franchises'),
  getLanguages: (schema) => api.get(`/franchises/${schema}/languages`)
};

// Cards API
export const cardsAPI = {
  getPending: (schema) => api.get(`/franchises/${schema}/cards`),

  // Nouvelle API: Traduire toutes les cartes et extraire les expressions (support multi-langues)
  translate: async (schema, targetLangs, onProgress) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/franchises/${schema}/cards/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ targetLangs }) // Array de langues
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la traduction');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim()) {
          try {
            const data = JSON.parse(line);

            if (onProgress) {
              onProgress(data);
            }

            if (data.type === 'complete') {
              result = {
                translationsByLang: data.translationsByLang, // { en: [...], es: [...] }
                statsByLang: data.statsByLang, // { en: { localWords, deeplWords }, ... }
                cards: data.cards,
                activeSaleSupports: data.activeSaleSupports
              };
            } else if (data.type === 'error') {
              throw new Error(data.details || data.error);
            }
          } catch (e) {
            console.error('Erreur parsing JSON:', e);
          }
        }
      }
    }

    return result;
  },

  // Valider et persister les traductions (support multi-langues)
  validate: async (schema, targetLangs, translationsByLang, cardUuids, activeSaleSupports, onProgress) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/franchises/${schema}/cards/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ targetLangs, translationsByLang, cardUuids, activeSaleSupports })
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la validation');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.trim()) {
          try {
            const data = JSON.parse(line);

            if (onProgress) {
              onProgress(data);
            }

            if (data.type === 'complete') {
              result = data.summary;
            } else if (data.type === 'error') {
              // Utiliser data.details si disponible (contient le vrai message d'erreur)
              const errorMessage = data.details || data.error || 'Erreur lors de la validation';
              throw new Error(errorMessage);
            }
          } catch (e) {
            console.error('Erreur parsing JSON:', e);
            // Re-throw l'erreur si c'est une erreur métier (pas une erreur de parsing)
            if (e.message !== 'Unexpected token' && !e.message.includes('JSON')) {
              throw e;
            }
          }
        }
      }
    }

    return result;
  }
};

// Logs API
export const logsAPI = {
  getAll: (params) => api.get('/logs', { params }),
  getStats: () => api.get('/logs/stats'),
  getDetail: (id) => api.get(`/logs/${id}`)
};

export default api;
